import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { refreshedClaim } from '../../stores/slices/reviewsSlice';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import { definitionCaptureContext } from '../../apis';
import type { DeckCheckRow, ReviewSection } from '../../types';
import { StatusChip } from '../../design/primitives';
import DefinitionCaptureCard from '../LandingAgent/DefinitionCaptureCard';
import TraceLink, { traceLinksOn, traceUrl } from '../agent-common/TraceLink';
import { useNumberPopover } from './NumberPopover';
import {
  DECK_RESULT_LABEL, DECK_RESULT_TONE, currentResult, deckCounts, deckCountsText, deckName, gradedAgainstDeck, mappedDefinition, mappingReproduced, sectionForDeckRow,
} from './reviewPage';

const defName = (row: DeckCheckRow) => row.resolved?.perspective_name || row.resolved?.metric || 'the deck';

/** The capture card wants a turn; a deck check row is not a conversation, but the card only reads the request and its id. */
function captureTurn(threadId: string, row: DeckCheckRow, perspective: string, context: Record<string, any>): LandingAgentTurn {
  return {
    id: `deckcheck:${threadId}:${row.id}`, userText: '', sentAt: 0, status: 'defining', result: null, events: [],
    clarification: null, error: null, definitionRequest: { sub_task_id: row.id, term: row.label, kind_hint: 'metric', perspective, ...context },
  } as unknown as LandingAgentTurn;
}

/**
 * The deck check (#568): the review was checked against the deck going out,
 * and says so above everything else — how many of its numbers matched, which
 * are stale, which the deck reads under another definition, which need you.
 * Show details opens the grading as a table; a different definition is settled
 * once, for this deck, with a signed mapping; an unknown term is defined in place.
 */
export default function DeckCheck({ threadId }: { threadId: string }) {
  const thread = useVoyagerStore((s) => s.savedThreads.find((t) => t.id === threadId));
  const busy = useVoyagerStore((s) => s.reviewsBusy[threadId]);
  const mapDeckDefinition = useVoyagerStore((s) => s.mapDeckDefinition);
  const pinClaimToReview = useVoyagerStore((s) => s.pinClaimToReview);
  const popover = useNumberPopover();
  const [open, setOpen] = useState(false);
  const [defining, setDefining] = useState<{ rowId: string; context: Record<string, any> | null; error?: string } | null>(null);
  const check = thread?.doc?.deck_check;
  if (!thread || !check || !check.rows.length) return null;
  const counts = deckCounts(thread, check);
  const clean = counts.matches === counts.total;
  const sessions = check.session_ids || [];
  const perspective = thread.doc?.perspective_id || thread.perspectiveId || '';

  const why = (e: React.MouseEvent<HTMLElement>, row: DeckCheckRow, section: ReviewSection | null) => {
    if (section) popover.open(refreshedClaim(section), e.currentTarget, null, { kind: 'review', thread_id: threadId, section_id: section.id });
    else if (row.claim) popover.open(row.claim, e.currentTarget);
  };
  const define = async (row: DeckCheckRow) => {
    if (defining?.rowId === row.id) { setDefining(null); return; }
    setDefining({ rowId: row.id, context: null });
    try {
      const context = await definitionCaptureContext(row.label, perspective);
      setDefining((d) => (d?.rowId === row.id ? { rowId: row.id, context: (context || {}) as Record<string, any> } : d));
    } catch (err: any) {
      setDefining((d) => (d?.rowId === row.id ? { rowId: row.id, context: null, error: err?.response?.data?.error || err?.message || 'Could not open the definition' } : d));
    }
  };

  return (
    <Box data-testid="deck-check" sx={{ display: 'grid', gap: 1.25 }}>
      <Box data-testid="deck-check-banner" sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', px: 2, py: 1.5, borderRadius: 3.5,
        border: '1px solid', borderColor: clean ? 'var(--line)' : 'color-mix(in srgb, var(--warn) 40%, var(--line))',
        background: clean ? 'var(--surface)' : 'linear-gradient(180deg, var(--warn-tint), transparent)',
      }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Checked against {check.artifact?.kind === 'report' ? 'the report' : 'the deck going out'}: {deckName(check)}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="deck-check-counts">{deckCountsText(counts)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {sessions.length === 1 && <TraceLink sessionId={sessions[0]} title="Open the grading's trace — every verdict, with the metric and SQL behind it" />}
          {sessions.length > 1 && traceLinksOn() && (
            <a href={traceUrl({ thread: threadId })} target="_blank" rel="noopener noreferrer" data-testid="deck-check-trace-pages" style={{ fontSize: 12, color: 'inherit' }}
              title="The deck was graded in pages, each its own trace — every session of this review, together">Trace · {sessions.length} pages</a>
          )}
          <Button size="small" variant="outlined" onClick={() => setOpen((o) => !o)} data-testid="deck-check-toggle" aria-expanded={open}>
            {open ? 'Hide details' : 'Show details'}
          </Button>
        </Box>
      </Box>
      {open && (
        <Box data-testid="deck-check-details" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper', overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 13,
            '& th, & td': { px: 1.75, py: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'left', verticalAlign: 'top' },
            '& th': { color: 'text.secondary', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, borderTop: 0 } }}>
            <thead><tr><th>Number</th><th>In the deck</th><th>Stroma</th><th>Result</th><th aria-label="Action" /></tr></thead>
            <tbody>
              {check.rows.map((row) => {
                const section = sectionForDeckRow(thread, row);
                const mapped = mappedDefinition(row, section);
                const result = currentResult(row, section, check);
                const stroma = row.verdict === 'stale' ? (row.stale?.fact_rendered || row.stroma)
                  : row.verdict === 'ambiguous' && !mapped ? (row.default?.rendered || row.stroma) : row.stroma;
                const note = row.verdict === 'stale' ? row.stale?.explanation
                  : row.verdict === 'ambiguous' ? `The deck uses ${defName(row)}'s definition (${row.resolved?.metric})${row.default ? `; the label alone reads ${row.default.metric} = ${row.default.rendered}` : ''}`
                    : row.verdict === 'unknown_term' ? `No definition of ${(row.unknown_words || []).join(', ') || row.label} in this workspace`
                      : result === 'needs' || result === 'skipped' ? row.reason : null;
                return (
                  <React.Fragment key={row.id}>
                    <tr data-testid="deck-check-row" data-result={result}>
                      <td>
                        <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{row.label}</Typography>
                        {note && (
                          <Typography variant="caption" title={note} data-testid="deck-check-note"
                            sx={{ color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxWidth: 420 }}>{note}</Typography>
                        )}
                        {row.where && <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>{row.where}</Typography>}
                      </td>
                      <td style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{row.deck ?? '—'}</td>
                      <td style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }} data-testid="deck-check-stroma">{stroma ?? '—'}</td>
                      <td><StatusChip status={DECK_RESULT_TONE[result]} label={DECK_RESULT_LABEL[result]} size="small" data-testid="deck-check-result" /></td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {mapped && !gradedAgainstDeck(mapped, check) ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, whiteSpace: 'normal', display: 'block', maxWidth: 260 }} data-testid="deck-check-mapped">
                              {defName(row)}'s definition used for this deck{mapped.name ? ` · ${mapped.name}` : ''}{mapped.at ? ` · ${String(mapped.at).slice(0, 10)}` : ''} — mapped after a refresh, so it was re-derived over this week, not graded against the deck's number
                            </Typography>
                            <Button size="small" onClick={(e) => why(e, row, section)} data-testid="deck-check-why">Why?</Button>
                          </Box>
                        ) : mapped && mappingReproduced(mapped) ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ color: 'var(--ok)', fontWeight: 600 }} data-testid="deck-check-mapped">
                              {defName(row)}'s definition used for this deck ✓{mapped.name ? ` · ${mapped.name}` : ''}{mapped.at ? ` · ${String(mapped.at).slice(0, 10)}` : ''}
                            </Typography>
                            {/* a mapped number is still a number: Why? opens it like any other row's */}
                            <Button size="small" onClick={(e) => why(e, row, section)} data-testid="deck-check-why">Why?</Button>
                          </Box>
                        ) : mapped ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ color: 'var(--bad)', fontWeight: 600, whiteSpace: 'normal', display: 'block', maxWidth: 260 }} data-testid="deck-check-mapped">
                              Mapped to {defName(row)}'s definition{mapped.name ? ` by ${mapped.name}` : ''}, but it re-derived to a different number ({mapped.verdict || 'not reproduced'}) — revoke it under Connections › source mappings to undo
                            </Typography>
                            <Button size="small" onClick={(e) => why(e, row, section)} data-testid="deck-check-why">Why?</Button>
                          </Box>
                        ) : row.verdict === 'ambiguous' ? (
                          section ? (
                            <Button size="small" variant="outlined" disabled={!!busy} onClick={() => mapDeckDefinition(threadId, section.id, row)} data-testid="deck-check-map">
                              Use {defName(row)}'s definition for this deck
                            </Button>
                          ) : row.claim ? (
                            <Button size="small" disabled={!!busy} data-testid="deck-check-pin"
                              onClick={() => pinClaimToReview(threadId, { ...row.claim, label: row.label }, { thread_id: null, thread_title: `reconciled from ${deckName(check)}`, ref: row.where || null })}>
                              Pin to review to map it
                            </Button>
                          ) : (
                            // reconcile makes a claim of a level, never of a change: a WoW or YoY cell has no number of its own on the review
                            <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="deck-check-unpinned">A change against another period — map the number it compares</Typography>
                          )
                        ) : row.verdict === 'unknown_term' ? (
                          <Button size="small" onClick={() => define(row)} data-testid="deck-check-define">{defining?.rowId === row.id ? 'Close' : 'Define'}</Button>
                        ) : (section || row.claim) && result !== 'needs' && result !== 'skipped' ? (
                          <Button size="small" onClick={(e) => why(e, row, section)} data-testid="deck-check-why">Why?</Button>
                        ) : null}
                      </td>
                    </tr>
                    {defining?.rowId === row.id && (
                      <tr data-testid="deck-check-capture">
                        <td colSpan={5} style={{ background: 'var(--surface2)' }}>
                          {defining.error ? <Typography variant="caption" color="error">{defining.error}</Typography>
                            : defining.context ? <DefinitionCaptureCard turn={captureTurn(threadId, row, perspective, defining.context)} />
                              : <Typography variant="caption" sx={{ color: 'text.secondary' }}>Opening the definition…</Typography>}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', px: 1.75, py: 1, color: 'text.secondary', borderTop: '1px solid', borderColor: 'divider' }}>
            Every number re-derived from the warehouse over {check.window ? `${check.window.start} → ${check.window.end}` : "the deck's own period"}
            {check.checked_at ? ` · checked ${String(check.checked_at).slice(0, 10)}` : ''} · a definition used for this deck is a signed source mapping; the next refresh reads it
          </Typography>
        </Box>
      )}
    </Box>
  );
}
